import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UrgencyBadge } from '@/components/shared/UrgencyBadge';
import { FrameworkBadge } from '@/components/shared/FrameworkBadge';
import type { ReviewSessionSummary } from '@/types/api';
import { cn } from '@/lib/utils';

interface ReviewSessionCardProps {
  readonly session: ReviewSessionSummary;
}

function formatDepartment(dept: string): string {
  return dept.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

const STATUS_BORDER: Record<string, string> = {
  pending: 'border-l-4 border-l-teal',
  in_review: 'border-l-4 border-l-compliance-medium',
  revision_requested: 'border-l-4 border-l-compliance-high',
};

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export function ReviewSessionCard({ session }: ReviewSessionCardProps) {
  const navigate = useNavigate();
  const borderClass = STATUS_BORDER[session.status] ?? '';

  const handleClick = () => {
    navigate(`/review/${session.sessionId}`);
  };

  return (
    <Card
      className={cn(
        'cursor-pointer transition-colors hover:bg-muted/30',
        borderClass,
      )}
      onClick={handleClick}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-2">
            {/* Row 1: Title + Urgency */}
            <div className="flex items-center gap-3">
              <p className="truncate text-base font-semibold text-foreground">
                {session.documentTitle}
              </p>
              <UrgencyBadge score={session.urgencyScore} className="shrink-0" />
            </div>

            {/* Row 2: Department + Frameworks + Revision count */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {formatDepartment(session.department)}
              </span>
              {(session.frameworksAffected ?? []).slice(0, 2).map((fw) => (
                <FrameworkBadge key={fw} framework={fw} />
              ))}
              {(session.frameworksAffected?.length ?? 0) > 2 && (
                <span className="text-xs text-muted-foreground">
                  +{session.frameworksAffected!.length - 2}
                </span>
              )}
              {session.revisionCount > 0 && (
                <span className="inline-flex items-center rounded-md bg-orange-500/15 px-2 py-0.5 text-xs font-semibold text-orange-400">
                  Rev {session.revisionCount}
                </span>
              )}
            </div>

            {/* Row 3: Assigned time */}
            <p className="text-xs text-muted-foreground">
              Assigned {formatRelativeTime(session.assignedAt)}
            </p>
          </div>

          {/* Review button */}
          <Button
            size="sm"
            className="shrink-0 bg-teal text-white hover:bg-teal/90"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            Review
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function ReviewSessionCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-3">
            <div className="h-5 w-3/4 animate-pulse rounded bg-muted" />
            <div className="flex gap-2">
              <div className="h-5 w-20 animate-pulse rounded bg-muted" />
              <div className="h-5 w-16 animate-pulse rounded bg-muted" />
            </div>
            <div className="h-3 w-24 animate-pulse rounded bg-muted" />
          </div>
          <div className="h-8 w-20 animate-pulse rounded bg-muted" />
        </div>
      </CardContent>
    </Card>
  );
}
